import React, { useRef, useState } from 'react'
import { Node } from 'reactflow'
import { BaseNodeData } from '../../base/types'
import { NodeTypes } from '../../../../../../shared/models/rule-node'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Col, Form, Row } from 'react-bootstrap'
import Input from '../../../../../../components/form/Input'
import 'ace-builds/src-noconflict/mode-golang'
import 'ace-builds/src-noconflict/theme-monokai'
import 'ace-builds/src-noconflict/ext-language_tools'
import SelectBase from '../../../../../../components/page-components/selects-entities/generic-select/SelectBase'
import { SelectableValue } from '../../../../../../components/page-components/selects-entities/generic-select/selectable-value'
import { NotificationTemplateID } from '../../../../../../shared/models/id/notification_template_id'
import {
  useLazyGetNotificationTemplateQuery,
  useLazyGetNotificationTemplatesQuery,
} from '../../../../../../store/kopiliot-api/notification-template'
import { NotificationRecipientID } from '../../../../../../shared/models/id/notification-recipient-i-d'
import {
  useLazyGetNotificationRecipientQuery,
  useLazyGetNotificationRecipientsQuery,
} from '../../../../../../store/kopiliot-api/notification-recipient'
import { MultiSelect } from '../../../../../../components/page-components/selects-entities/generic-select/Select'
import _ from 'lodash'
import { NotificationType } from '../../../../../../shared/models/notification.model'

export interface ExternalSendNotificationConfiguration {
  templateID: NotificationTemplateID
  recipientIDs: NotificationRecipientID[]
}

interface ExternalSendNotificationFormProps {
  onSubmit: (
    data: Node<
      BaseNodeData<ExternalSendNotificationConfiguration>,
      NodeTypes.EXTERNAL_SEND_NOTIFICATION
    >
  ) => void
  defaultValues?: Node<
    BaseNodeData<ExternalSendNotificationConfiguration>,
    NodeTypes.EXTERNAL_SEND_NOTIFICATION
  >
}

const ExternalSendNotificationForm = ({
  onSubmit,
  defaultValues,
}: ExternalSendNotificationFormProps) => {
  const [templateOptions, setTemplateOptions] = React.useState<
    SelectableValue<NotificationTemplateID>[] | undefined
  >(undefined)
  const [recipientOptions, setRecipientOptions] = React.useState<
    SelectableValue<NotificationRecipientID>[] | undefined
  >(undefined)
  const [getTemplates, resultGetTemplates] =
    useLazyGetNotificationTemplatesQuery()
  const [getTemplate, resultGetTemplate] = useLazyGetNotificationTemplateQuery()
  const [getRecipients, resultGetRecipients] =
    useLazyGetNotificationRecipientsQuery()
  const [getRecipient, resultGetRecipient] =
    useLazyGetNotificationRecipientQuery()
  const [show, setShow] = useState(false)
  const target = useRef(null)
  const form = useForm<
    Node<
      BaseNodeData<ExternalSendNotificationConfiguration>,
      NodeTypes.EXTERNAL_SEND_NOTIFICATION
    >
  >({
    defaultValues,
  })
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = form
  const watchRecipientIDs = form.watch('data.configuration.recipientIDs')
  // First render
  React.useEffect(() => {
    if (defaultValues?.data.configuration?.templateID) {
      getTemplate(defaultValues?.data.configuration?.templateID)
        .unwrap()
        .then((value) => {
          setTemplateOptions([
            {
              value: value.id,
              label: value.name,
            },
          ])
        })
    }
  }, [])

  React.useEffect(() => {
    const fetchRecipients = async () => {
      if (defaultValues?.data.configuration?.recipientIDs) {
        try {
          const recipients = await Promise.all(
            defaultValues.data.configuration.recipientIDs.map(async (id) => {
              const item = await getRecipient(id).unwrap()
              return {
                value: item.id,
                label: item.name,
              }
            })
          )
          setRecipientOptions(recipients)
        } catch (error) {
          console.error('Error fetching recipients', error)
        }
      }
    }

    fetchRecipients()
  }, [])

  React.useEffect(() => {
    setTemplateOptions(
      resultGetTemplates.data?.rows.map((v) => {
        return {
          value: v.id,
          label: v.name,
        }
      })
    )
  }, [resultGetTemplates.data])
  React.useEffect(() => {
    setRecipientOptions((prevState) => {
      let result: SelectableValue<NotificationRecipientID>[] = []
      const newValues = resultGetRecipients.data?.rows.map((v) => {
        return {
          value: v.id,
          label: v.name,
        }
      })
      if (newValues) {
        result.push(...newValues)
      }
      if (prevState) {
        result = _.unionBy(result, prevState, (v) => {
          return v.value
        })
      }
      console.log(result)
      return result
    })
  }, [resultGetRecipients.data])
  return (
    <FormProvider {...form}>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} id={'form_rule_node'}>
        <Row className={''}>
          <Form.Group as={Col} lg={6} md={12}>
            <Input
              register={register('data.name', {
                required: 'Le nom est obligatoire',
              })}
              label={'Nom'}
              error={errors?.data?.name}
            />
          </Form.Group>
        </Row>
        <Row className={''}>
          <Form.Group as={Col} lg={12}>
            <Form.Label ref={target}>Template </Form.Label>
            <Controller
              control={form.control}
              name={'data.configuration.templateID'}
              rules={{ required: 'Please select a template' }}
              render={({ field }) => {
                return (
                  <SelectBase
                    isLoading={resultGetTemplates.isLoading}
                    placeholder={'Rechercher...'}
                    options={templateOptions}
                    onInputChange={(value) => {
                      getTemplates({
                        offset: 0,
                        limit: 100,
                        textSearch: value,
                        notificationType: NotificationType.RULE_NODE,
                      })
                    }}
                    isClearable={true}
                    debounceDelay={1000}
                    minCharsForSearch={2}
                    value={field.value ? { value: field.value } : undefined}
                    onChange={(
                      value: SelectableValue<NotificationTemplateID>
                    ) => {
                      if (value) {
                        field.onChange(value?.value)
                      }
                    }}
                    valueComparator={(a, b) => {
                      return a?.value?.id === b?.value?.id
                    }}
                    errorMessage={
                      errors.data?.configuration?.templateID?.message
                    }
                  />
                )
              }}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} lg={6} md={12}>
            <Form.Label htmlFor={'templateID'}>Recipients</Form.Label>
            <Controller
              name={'data.configuration.recipientIDs'}
              control={form.control}
              render={({ field }) => {
                return (
                  <MultiSelect
                    isLoading={resultGetRecipients.isLoading}
                    placeholder={'Rechercher...'}
                    options={recipientOptions}
                    onInputChange={(value) => {
                      getRecipients({
                        offset: 0,
                        limit: 100,
                        textSearch: value,
                      })
                    }}
                    isClearable={true}
                    debounceDelay={2000}
                    minCharsForSearch={2}
                    value={field.value?.map((v: NotificationRecipientID) => {
                      return { value: v }
                    })}
                    valueComparator={(a, b) => {
                      return a?.value?.id === b?.value?.id
                    }}
                    errorMessage={
                      errors.data?.configuration?.recipientIDs?.message
                    }
                    onChange={(value) => {
                      field.onChange(
                        value?.map((v) => {
                          return v.value
                        })
                      )
                      /*const result: NotificationID[] = []
                      if (field.value && field.value.length > 0) {
                        result.push(...field.value)
                      }
                      if (value) {
                        result.push(
                          ...value
                            ?.filter((item) => item.value)
                            .map((v) => v.value!)
                        )
                        field.onChange(result)
                      }*/
                    }}
                  />
                )
              }}
            />
          </Form.Group>
        </Row>
        <Row className={''}>
          <Form.Group as={Col} lg={12}>
            <Input
              register={register('data.description')}
              label={'Description'}
              type={'textarea'}
            />
          </Form.Group>
        </Row>
      </Form>
    </FormProvider>
  )
}

export default ExternalSendNotificationForm
